import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import SearchBlockList from "@Front/Components/DesignSystem/SearchBlockList";
import type { IBlock } from "@Front/Components/DesignSystem/SearchBlockList/BlockList/Block";
import type { CustomerLoginCandidateDto } from "@Front/Api/Public/emailAuthCandidatesApi";
import classes from "@Front/Components/Layouts/Login/classes.module.scss";
import { useMemo } from "react";
import { CUSTOMER_TARGET_CHOICE_SUBTITLE_DEFAULT } from "@Front/Components/Layouts/Login/helpers/customerLoginNeedsChoiceMessages";
import { applyEnsoFolderAsOperationFr } from "@Front/Utils/applyEnsoFolderAsOperationFr";

function formatContactDisplayName(c: CustomerLoginCandidateDto): string {
	const parts = [c.contactFirstName?.trim(), c.contactLastName?.trim()].filter((p): p is string => !!p && p.length > 0);
	return parts.join(" ").trim();
}

function buildTargetChoiceBlockTexts(c: CustomerLoginCandidateDto): { primaryText: string; secondaryText: string } {
	const displayName = formatContactDisplayName(c);
	const folderLine = applyEnsoFolderAsOperationFr(`Dossier ${c.folderNumber}`);
	const tail = [c.officeName, c.roleLabel].filter(Boolean).join(" · ");
	if (displayName.length > 0) {
		return {
			primaryText: displayName,
			secondaryText: [folderLine, tail].filter(Boolean).join(" · "),
		};
	}
	return {
		primaryText: folderLine,
		secondaryText: tail,
	};
}

type Props = {
	candidates: CustomerLoginCandidateDto[];
	onSelect: (row: CustomerLoginCandidateDto) => void;
	isSubmitting: boolean;
	/** Si absent, libellé par défaut (connexion directe après choix d’étude). */
	subtitle?: string;
};

export default function StepCustomerTargetChoice(props: Props): React.JSX.Element {
	const { candidates, onSelect, isSubmitting, subtitle } = props;
	const blocks: IBlock[] = useMemo(
		() =>
			candidates.map((c) => {
				const { primaryText, secondaryText } = buildTargetChoiceBlockTexts(c);
				return {
					id: `${c.customerUid}:${c.folderUid}`,
					primaryText,
					secondaryText,
				};
			}),
		[candidates],
	);

	const handleBlock = (block: IBlock): void => {
		if (isSubmitting) return;
		const [customerUid, folderUid] = block.id.split(":");
		const row = candidates.find((c) => c.customerUid === customerUid && c.folderUid === folderUid);
		if (row) {
			void onSelect(row);
		}
	};

	return (
		<div className={classes["root"]}>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY} className={classes["disambiguation-hint"]}>
				{subtitle ?? CUSTOMER_TARGET_CHOICE_SUBTITLE_DEFAULT}
			</Typography>
			<div className={classes["disambiguation-list"]}>
				<SearchBlockList blocks={blocks} onSelectedBlock={handleBlock} fullwidth />
			</div>
		</div>
	);
}
